import classNames from "classnames";
import { TemplateKey } from "PFApp/constants/templates";
import { Animate } from "PFComponents/containers";
import { ReadOnlyField } from "PFComponents/read_only_field";
import { Tile } from "PFComponents/tile";
import { getProfileName } from "PFCore/helpers/profile";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useDateFormatter } from "PFCore/hooks";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { Activity, FeatureFlag } from "PFCore/types";
import { useTranslation } from "react-i18next";

import { useActivityFormStore } from "../../activity_form_store";
import { useEngagementTemplate } from "../../hooks/use_engagement_template";
import css from "./parent_engagement_bar.module.scss";

type ParentEngagementBarProps = {
  selectedParentActivity: Activity;
  isSmallScreen: boolean;
};

export const ParentEngagementBar = ({ isSmallScreen, selectedParentActivity }: ParentEngagementBarProps) => {
  const { t } = useTranslation("activities");
  const { isAudit } = useActivityFormStore((state) => state.activityStatus);
  const { formatDate } = useDateFormatter();
  const { getBookingCategory } = useBookingCategories();
  const isFeatureEnabled = useIsFeatureEnabled();
  const engagementTemplate = useEngagementTemplate();

  const engagementKey = isAudit ? TemplateKey.AuditEngagement : TemplateKey.Engagement;

  const assignee = selectedParentActivity.assignee?.profile;
  const bookingCategory = getBookingCategory(selectedParentActivity.booking_category_id)?.display_as;
  const isBookingModuleEnabled = isFeatureEnabled(FeatureFlag.BookingModule);
  const isBookingCategoryVisible =
    isBookingModuleEnabled && !!engagementTemplate?.schema?.properties?.booking_category_id;

  return (
    <Animate className={classNames({ [css.sticky]: !isSmallScreen })}>
      <Tile variant="selected" paddingVariant="lg">
        <div className={css.grid}>
          <ReadOnlyField
            label={t(`templates.${engagementKey}`)}
            value={selectedParentActivity.name}
            classes={{ root: css.column }}
            clipOverflow
          />
          <ReadOnlyField
            label={t("form.parentActivityBar.expiryDate")}
            value={formatDate(selectedParentActivity.post_until)}
            classes={{ root: css.column }}
            clipOverflow
          />
          <ReadOnlyField
            label={t("form.parentActivityBar.assignee")}
            value={assignee ? getProfileName(assignee) : "-"}
            classes={{ root: css.column }}
            clipOverflow
          />
          {isBookingCategoryVisible && (
            <ReadOnlyField
              label={t("form.parentActivityBar.bookingCategory")}
              value={bookingCategory ?? "-"}
              classes={{ root: css.column }}
              clipOverflow
            />
          )}
        </div>
      </Tile>
      <div className={css.bottomSpacing} />
    </Animate>
  );
};
